import { useEffect, useMemo, useState } from 'react';
import { formatEther, parseUnits } from 'viem';
import {
  useAccount,
  useBalance,
  usePublicClient,
  useReadContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';
import { RiCoinsLine, RiErrorWarningLine, RiLoader4Line, RiSparkling2Line } from '@remixicon/react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card';
import { Button } from '@/components/button';
import { Badge } from '@/components/badge';
import { MINT_STATS_ABI, MINT_STATS_CONTRACT } from '@/abis/mint-stats';

type TxHash = `0x${string}`;

function prettyMintError(err: unknown) {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  const lower = msg.toLowerCase();

  if (lower.includes('user rejected') || lower.includes('userrejected') || lower.includes('rejected')) {
    return 'Transaction rejected in wallet.';
  }
  // NFTDaoV3-style require codes:
  // "1" = exceed max supply, "2" = msg.value mismatch
  if (lower.includes('reverted') && (lower.includes('"1"') || lower.includes(' 1 ') || lower.endsWith('1'))) {
    return 'Sold out: no NFT stock remaining.';
  }
  if (lower.includes('reverted') && (lower.includes('"2"') || lower.includes(' 2 ') || lower.endsWith('2'))) {
    return 'Incorrect payment amount. Please refresh to fetch the latest mint price.';
  }

  return msg || 'Mint failed.';
}

export default function NftMintSidebar({
  isOpen,
  onClose,
  onMinted,
}: {
  isOpen: boolean;
  onClose: () => void;
  onMinted?: () => void;
}) {
  const { address } = useAccount();
  const publicClient = usePublicClient();

  const [txHash, setTxHash] = useState<TxHash | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [gasPrice, setGasPrice] = useState<bigint | null>(null);
  const [estimatedGas, setEstimatedGas] = useState<bigint | null>(null);

  const contractReady = !!MINT_STATS_CONTRACT;

  const { data: balance } = useBalance({
    address,
    query: { enabled: !!address && isOpen },
  });

  const { data: mintPrice, refetch: refetchMintPrice } = useReadContract({
    address: MINT_STATS_CONTRACT,
    abi: MINT_STATS_ABI,
    functionName: 'getMintPrice',
    query: { enabled: contractReady && isOpen },
  }) as { data: bigint | undefined; refetch: () => void };

  const { data: stock } = useReadContract({
    address: MINT_STATS_CONTRACT,
    abi: MINT_STATS_ABI,
    functionName: 'getNftStock',
    query: { enabled: contractReady && isOpen },
  }) as { data: bigint | undefined };

  const { writeContractAsync, isPending: isWritePending } = useWriteContract();

  const { isLoading: isTxPending, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
    query: { enabled: !!txHash },
  });

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setTxHash(undefined);
  }, [isOpen]);

  useEffect(() => {
    if (!isTxSuccess) return;
    setTxHash(undefined);
    setError(null);
    onMinted?.();
    void refetchMintPrice();
  }, [isTxSuccess, onMinted, refetchMintPrice]);

  // Gas price: keep separate + resilient (no perpetual calculating)
  useEffect(() => {
    if (!isOpen || !publicClient) {
      setGasPrice(null);
      return;
    }

    const fetchGasPrice = async () => {
      try {
        const gp = await publicClient.getGasPrice();
        setGasPrice(gp);
      } catch (e) {
        console.error('Error fetching gas price:', e);
        setGasPrice(parseUnits('50', 9));
      }
    };

    fetchGasPrice().then();
  }, [isOpen, publicClient]);

  // Gas estimate: depends on mint price + wallet address
  useEffect(() => {
    if (!isOpen || !publicClient || !address || !mintPrice || !contractReady) {
      setEstimatedGas(null);
      return;
    }

    const estimate = async () => {
      try {
        const gas = await publicClient.estimateContractGas({
          account: address,
          address: MINT_STATS_CONTRACT,
          abi: MINT_STATS_ABI,
          functionName: 'mintNFT',
          value: mintPrice,
        });
        setEstimatedGas(gas);
      } catch (e) {
        console.error('Error estimating mint gas:', e);
        setEstimatedGas(BigInt(240000));
      }
    };

    estimate().then();
  }, [isOpen, publicClient, address, mintPrice, contractReady]);

  const pricing = useMemo(() => {
    if (!mintPrice) return null;
    if (!estimatedGas || !gasPrice) return { mintPrice, gasCost: null as bigint | null, totalCost: null as bigint | null };
    const gasCost = estimatedGas * gasPrice;
    return { mintPrice, gasCost, totalCost: mintPrice + gasCost };
  }, [mintPrice, estimatedGas, gasPrice]);

  const hasEnoughBalance = useMemo(() => {
    if (!balance?.value || !pricing?.totalCost) return null;
    return balance.value >= pricing.totalCost;
  }, [balance?.value, pricing?.totalCost]);

  const isBusy = isWritePending || isTxPending;
  const isSoldOut = stock !== undefined ? stock <= BigInt(0) : false;
  const isDisabled =
    !contractReady || !address || isBusy || !mintPrice || isSoldOut || (hasEnoughBalance === false);

  const handleMint = async () => {
    if (!address || !mintPrice || !contractReady) return;
    try {
      setError(null);
      setTxHash(undefined);

      const hash = await writeContractAsync({
        address: MINT_STATS_CONTRACT,
        abi: MINT_STATS_ABI,
        functionName: 'mintNFT',
        value: mintPrice,
      });

      setTxHash(hash as TxHash);
    } catch (e) {
      setError(prettyMintError(e));
    }
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={() => {
        onClose();
      }}
    >
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-primary">Mint / Buy NFT</SheetTitle>
          <SheetDescription>
            Mint a new NFT by paying the exact mint price in LRS. We’ll show an estimated gas cost before you confirm.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          {!contractReady && (
            <div className="rounded-md border border-border bg-sidebar/60 px-3 py-2 text-sm text-muted-foreground">
              Set <code className="text-xs">VITE_MINT_STATS_CONTRACT_ADDRESS</code> to enable minting.
            </div>
          )}

          <Card className="bg-sidebar border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <RiSparkling2Line className="size-5 text-primary" aria-hidden />
                Mint details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Mint price</span>
                <span className="font-semibold tabular-nums">
                  {mintPrice ? `${parseFloat(formatEther(mintPrice)).toFixed(6)} LRS` : '—'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Estimated gas</span>
                <span className="font-medium tabular-nums">
                  {pricing?.gasCost !== null && pricing?.gasCost !== undefined
                    ? `${parseFloat(formatEther(pricing.gasCost)).toFixed(6)} LRS`
                    : gasPrice
                      ? 'Estimating…'
                      : '—'}
                </span>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-sm text-muted-foreground">Estimated total</span>
                <span className="font-semibold tabular-nums">
                  {pricing?.totalCost ? `${parseFloat(formatEther(pricing.totalCost)).toFixed(6)} LRS` : '—'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Stock</span>
                {stock === undefined ? (
                  <span className="text-sm text-muted-foreground">—</span>
                ) : stock > BigInt(0) ? (
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">
                    {stock.toString()} available
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-red-500/40 text-red-300">
                    Sold out
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Wallet balance</span>
                <span className="text-sm font-medium tabular-nums">
                  {balance?.value ? `${parseFloat(formatEther(balance.value)).toFixed(6)} LRS` : address ? '—' : 'Connect wallet'}
                </span>
              </div>

              {hasEnoughBalance === false && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  Insufficient balance for mint + estimated gas.
                </div>
              )}
            </CardContent>
          </Card>

          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 flex gap-2">
              <RiErrorWarningLine className="size-4 mt-0.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{error}</span>
            </div>
          )}

          <Card className="bg-sidebar border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <RiCoinsLine className="size-5 text-primary" aria-hidden />
                Action
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                type="button"
                disabled={isDisabled}
                onClick={() => void handleMint()}
                className="w-full cursor-pointer bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-colors duration-200"
              >
                {isBusy ? (
                  <>
                    <RiLoader4Line className="size-4 animate-spin mr-2 inline" />
                    {isTxPending ? 'Confirming…' : 'Submitting…'}
                  </>
                ) : (
                  'Mint NFT'
                )}
              </Button>

              {!address && (
                <div className="text-xs text-muted-foreground">
                  Connect your wallet to mint.
                </div>
              )}
              {isSoldOut && (
                <div className="text-xs text-muted-foreground flex items-start gap-2">
                  <RiErrorWarningLine className="size-4 mt-0.5 shrink-0 text-red-300" aria-hidden />
                  <span>Minting is currently sold out.</span>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="text-xs text-muted-foreground flex items-start gap-2">
            <RiErrorWarningLine className="size-4 mt-0.5 shrink-0" aria-hidden />
            <span>
              Minting requires paying the <span className="font-medium text-foreground/80">exact</span> mint price. If the price
              changes between quote and confirm, refresh and try again.
            </span>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

